import { z } from 'zod/v4';

import { normalizePaymentFormWidgetArgs } from './payment-form-widget-args.normalization';
import { partialPaymentFormWidgetDataSchema } from './partial-payment-form-widget-data.schema';

/**
 * Creating a payment form — the visual and textual half of one, without the payment method.
 *
 * **Why this exists now, having been refused before.** The objection was never bindings, it was that
 * a payment form with no connected method cannot charge anyone: the published form reads
 * `payment_method_id` and `type` through truthiness, and the entity factory's default `type: 0`
 * (PayPal) is falsy, so a form built from those defaults submits a payment naming no method — and
 * looks configured while doing it. What changed is finding that the EDITOR has this state too:
 * `PaymentFormControlComponent.seedDefaultPaymentElement()` writes `paymentElements: []` outright
 * when the account has no connected methods. So "a payment form without a method" is a state the
 * product genuinely produces — and, crucially, one it makes NOISY: with no first element, the
 * editor's own `payment_form_invalid` check fires and the user is told the form is incomplete every
 * time they save.
 *
 * So this tool builds that state, not the disguised one. The empty list is the whole point: it is
 * the difference between a form the user is told to finish and a form that looks finished and
 * silently takes no money.
 *
 * **The price is refused rather than dropped.** `amount`/`currency` live on the payment element, and
 * there is no element yet. Accepting them would mean materialising the fake-PayPal entry this tool
 * exists to avoid; omitting them from the schema would let a plain `z.object()` strip them SILENTLY,
 * which is the failure mode this codebase treats as the worst of the three (a stripped field is a
 * success-reporting no-op; a rejected one is something the model can act on). So they are declared
 * and refused, with a message that says what to do instead.
 */
const createPaymentFormWidgetDataSchema = partialPaymentFormWidgetDataSchema
    .extend({
        paymentFormName: z
            .string()
            .min(1)
            .optional()
            .describe('REQUIRED. Heading above the form — visible on the published page, not an internal name.'),
        productName: z
            .string()
            .min(1)
            .optional()
            .describe(
                'REQUIRED. What is being sold, visible on the page and sent to the payment provider as ' +
                    'the order name.'
            ),
        buttonLabel: z
            .string()
            .min(1)
            .optional()
            .describe('REQUIRED. Text on the submit button, in the language of the page — e.g. "Оплатить".'),
    })
    .superRefine((data, ctx) => {
        // Checked here rather than as required fields, so a bare call is told about ALL of them at
        // once. zod skips `superRefine` when the base object fails, and with four requirements the
        // staircase that produced would cost four round trips for one widget — measured on the
        // subscription form, which has two (see its schema for the run).
        const required = [
            ['paymentFormName', 'the heading above the form'],
            ['productName', 'what is being sold'],
            ['buttonLabel', 'the text on the submit button'],
        ] as const;

        for (const [field, what] of required) {
            if (!data[field]?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    message: `a new payment form needs ${field}: ${what}, in the language of the page.`,
                    path: [field],
                });
            }
        }

        if (data.successMessage === undefined && data.successLink === undefined) {
            ctx.addIssue({
                code: 'custom',
                message:
                    'a new payment form needs a post-submit outcome: send successLink (where the buyer ' +
                    'goes after paying) or successMessage. Without either, a successful payment shows ' +
                    'an empty message.',
                path: ['successLink'],
            });
        }

        for (const field of ['amount', 'currency'] as const) {
            if (data[field] !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        `a new payment form has no payment method yet, and the price lives on the method — ` +
                        `so ${field} cannot be set at creation. Create the form, tell the user to pick a ` +
                        `payment method in its settings, then set the price with update_payment_form_widget.`,
                    path: [field],
                });
            }
        }
    });

const createPaymentFormWidgetObjectSchema = z.object({
    columnId: z.string().describe('Id of the column to create the payment form in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the column's widget list (0-based). Omit to append at the end."),
    data: createPaymentFormWidgetDataSchema.describe(
        'Payment form data. The heading, the product name, the button label and a post-submit outcome ' +
            'are required; the price and the payment method are not settable here — see amount.'
    ),
});

export const createPaymentFormWidgetArgsSchema = z.preprocess(
    normalizePaymentFormWidgetArgs,
    createPaymentFormWidgetObjectSchema
);
